import React, { useEffect, useState } from 'react';

import { LoadingSwitch } from '~/components/core';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import type { PhoneProfileListResponse } from '~/server_shared/openapi/data-contracts';

import { useSysconfig } from '../../SystemConfigurationScreen';

import PhoneProfileDetailView from './PhoneProfileDetailView/PhoneProfileDetailView';
import OrgPhoneSurveyCard from './PhoneProfileListView/OrgPhoneSurveyCard';
import PhoneProfileListView from './PhoneProfileListView/PhoneProfileListView';

const PhoneConfigurationTab: React.FC = () => {
  // useSysconfig is exported from a JSX file without types; cast at the boundary.
  const { organization, reloadOperationalDetails } = useSysconfig() as {
    organization: OrganizationModel;
    reloadOperationalDetails: () => Promise<void>;
  };
  const [viewedProfileId, setViewedProfileId] = useState<number | null>(null);

  const {
    isLoading: isLoadingProfiles,
    isError: isErrorProfiles,
    data: profilesData,
    reloadData: reloadProfiles,
  } = useDataFetcher<PhoneProfileListResponse>(`/api/v1/organizations/${organization.id}/phone_profiles`);

  const {
    isLoading: isLoadingOrgPhoneSurveyConfiguration,
    isError: isErrorOrgPhoneSurveyConfiguration,
    data: orgPhoneSurveyConfiguration,
    reloadData: reloadOrgPhoneSurveyConfiguration,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useDataFetcher<any>(`/api/v1/organizations/${organization.id}/phone_survey_configuration`);

  const profiles = profilesData?.phone_profiles ?? [];
  const orgPhoneSurveyConfigurationId =
    orgPhoneSurveyConfiguration && typeof orgPhoneSurveyConfiguration.id === 'number'
      ? orgPhoneSurveyConfiguration.id
      : null;

  const viewedProfile = viewedProfileId === null ? null : profiles.find((p) => p.id === viewedProfileId);
  // Profile was deleted or no longer accessible — return to list.
  useEffect(() => {
    if (viewedProfileId !== null && !viewedProfile && !isLoadingProfiles && !isErrorProfiles) {
      setViewedProfileId(null);
    }
  }, [viewedProfileId, viewedProfile, isLoadingProfiles, isErrorProfiles]);

  return (
    <LoadingSwitch isLoading={isLoadingProfiles && !profilesData} isError={isErrorProfiles}>
      {viewedProfile ? (
        <PhoneProfileDetailView
          profile={viewedProfile}
          organizationId={organization.id}
          orgPhoneSurveyConfigurationId={orgPhoneSurveyConfigurationId}
          isLoadingOrgPhoneSurveyConfiguration={isLoadingOrgPhoneSurveyConfiguration}
          onBackToList={() => setViewedProfileId(null)}
          reloadProfiles={reloadProfiles}
          reloadOperationalDetails={reloadOperationalDetails}
        />
      ) : (
        <PhoneProfileListView
          profiles={profiles}
          organizationId={organization.id}
          onOpenProfile={setViewedProfileId}
          reloadProfiles={reloadProfiles}
        >
          <OrgPhoneSurveyCard
            organizationId={organization.id}
            surveyConfiguration={orgPhoneSurveyConfiguration ?? {}}
            isLoading={isLoadingOrgPhoneSurveyConfiguration}
            isError={isErrorOrgPhoneSurveyConfiguration}
            reloadSurveyConfiguration={reloadOrgPhoneSurveyConfiguration}
          />
        </PhoneProfileListView>
      )}
    </LoadingSwitch>
  );
};

export default PhoneConfigurationTab;
